import React from 'react';
import { motion } from 'framer-motion';
import phoneMockup from '../../assets/tattoo-ai-inapp.png';
import tattooAILogo from '../../assets/images/tattoo_ai_logo.png';

const TattooAIPage = () => {
    const features = [
        { title: 'AI Tattoo Design', text: 'Generate unique and personalized tattoo designs using advanced AI technology.', icon: '🎨' },
        { title: 'Style Transfer', text: 'Transform your ideas into various tattoo styles - traditional, minimalist, realistic, and more.', icon: '✨' },
        { title: 'Preview on Body', text: 'See how your tattoo will look on your body before getting inked.', icon: '👤' },
        { title: 'Save & Export', text: 'Save your favorite designs and export them in high quality for your tattoo artist.', icon: '💾' },
    ];

    return (
        <div className="min-h-screen bg-dark pt-24 pb-16">
            {/* Background orbs */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 bg-accent-orange opacity-15 rounded-full blur-3xl" />
                <div className="absolute top-60 right-20 w-96 h-96 bg-accent-pink opacity-10 rounded-full blur-3xl" />
                <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-accent-purple opacity-10 rounded-full blur-3xl" />
            </div>

            <div className="max-w-6xl mx-auto px-6 relative z-10">
                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="flex flex-col lg:flex-row items-center gap-12 mb-16"
                >
                    {/* Left Content */}
                    <div className="flex-1 text-center lg:text-left">
                        <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="inline-block px-4 py-2 rounded-full bg-dark-card border border-accent-orange text-sm font-custom2 text-accent-orange mb-4"
                        >
                            NEW
                        </motion.span>

                        <div className="flex items-center gap-3 justify-center lg:justify-start mb-2">
                            <img src={tattooAILogo} alt="TattooAI Logo" className="w-12 h-12 md:w-14 md:h-14 rounded-xl" />
                            <h1 className="text-4xl md:text-5xl font-custom1 text-white">
                                TattooAI
                            </h1>
                        </div>
                        <span className="bg-gradient-to-r from-accent-orange to-accent-pink bg-clip-text text-transparent font-custom2 text-xl block mb-6">
                            Design Your Perfect Tattoo with AI
                        </span>

                        <p className="text-lg font-custom2 text-gray-400 mb-8 leading-relaxed">
                            Create stunning, unique tattoo designs powered by artificial intelligence. From traditional to modern styles, bring your tattoo ideas to life before getting inked.
                        </p>

                        {/* Store Badges */}
                        <div className="flex gap-4 justify-center lg:justify-start">
                        </div>
                    </div>

                    {/* Right - Phone Mockup */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="flex-1 flex justify-center"
                    >
                        <div className="relative w-64 md:w-72">
                            {/* Glow effect */}
                            <div className="absolute inset-0 bg-gradient-to-b from-accent-orange to-accent-pink opacity-25 blur-3xl scale-110" />

                            {/* Phone Frame */}
                            <div className="relative bg-gradient-to-b from-gray-700 via-gray-800 to-gray-900 rounded-[3rem] p-[3px] shadow-2xl">
                                <div className="bg-black rounded-[2.8rem] p-1.5">
                                    <div className="relative bg-black rounded-[2.4rem] overflow-hidden aspect-[9/19.5]">
                                        {/* Dynamic Island */}
                                        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20">
                                            <div className="w-24 h-8 bg-black rounded-full flex items-center justify-center gap-3 shadow-lg">
                                                <div className="w-2.5 h-2.5 rounded-full bg-gray-900 ring-1 ring-gray-800" />
                                                <div className="w-3 h-3 rounded-full bg-gray-900 ring-1 ring-gray-700" />
                                            </div>
                                        </div>
                                        <div className="absolute inset-0">
                                            <img src={phoneMockup} alt="TattooAI App" className="w-full h-full object-cover object-top" />
                                        </div>
                                    </div>
                                </div>
                                {/* Side buttons */}
                                <div className="absolute -left-[3px] top-28 w-[4px] h-8 bg-gradient-to-b from-gray-600 to-gray-700 rounded-l-md" />
                                <div className="absolute -left-[3px] top-40 w-[4px] h-14 bg-gradient-to-b from-gray-600 to-gray-700 rounded-l-md" />
                                <div className="absolute -left-[3px] top-56 w-[4px] h-14 bg-gradient-to-b from-gray-600 to-gray-700 rounded-l-md" />
                                <div className="absolute -right-[3px] top-36 w-[4px] h-20 bg-gradient-to-b from-gray-600 to-gray-700 rounded-r-md" />
                            </div>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Features */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="mb-16"
                >
                    <h2 className="text-2xl font-custom1 text-white mb-8 text-center">Features</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {features.map((feature, idx) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.4 + idx * 0.1 }}
                                className="bg-dark-card border border-gray-600 rounded-2xl p-6 hover:border-accent-orange transition-colors duration-300 flex items-start gap-4"
                            >
                                <span className="text-3xl">{feature.icon}</span>
                                <div>
                                    <h3 className="text-lg font-custom1 text-white mb-2">{feature.title}</h3>
                                    <p className="text-sm font-custom2 text-gray-400">{feature.text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Policies */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="text-center mb-10"
                >
                    <p className="text-gray-500 font-custom2 text-sm">
                        By using TattooAI, you agree to our{' '}
                        <a href="/tattooai/privacy-policy" className="text-accent-orange hover:underline">Privacy Policy</a>,{' '}
                        <a href="/tattooai/terms-and-conditions" className="text-accent-orange hover:underline">Terms & Conditions</a>, and{' '}
                        <a href="/tattooai/eula" className="text-accent-orange hover:underline">EULA</a>.
                    </p>
                </motion.div>

                {/* Footer */}
                <div className="text-center">
                    <p className="text-gray-600 font-custom2 text-sm">© 2025 TattooAI – All rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

export default TattooAIPage;
